<template>
  <div class="app-container">
    <header>
      <div class="header-content">
        <div class="logo-section">
          <img v-if="theme" src="https://mj-uploads.oss-cn-shenzhen.aliyuncs.com/public/aiDesign/System_Default/logo.png" alt="待加载" @click="gohome">
          <img v-else src="https://mj-uploads.oss-cn-shenzhen.aliyuncs.com/public/aiDesign/System_Default/dark_logo.png" alt="待加载" @click="gohome">
        </div>

        <!-- GPU 实时监控 -->
        <gpu-monitor />

        <div class="header-spacer"></div>
        <div class="header-actions">
          <!-- 返回主页按钮 -->
          <div class="home-btn" @click="gohome">
            <span class="iconfont">&#xe632;</span>
            <span>返回主页</span>
          </div>
          <!-- 生图记录按钮 -->
          <div class="header-btn" @click="handleGoToImageLog">
            <span class="iconfont">&#xe605;</span>
            <span>生图记录</span>
          </div>
          <!--支持与反馈按钮 -->
          <div class="header-btn" @click="handleOpenFeedback">
            <span class="iconfont">&#xe67c;</span>
            <span>支持与反馈</span>
          </div>
          <!-- 用户头像 -->
          <div class="user-avatar">
            <img :src="avatar" alt="待加载">
          </div>
        </div>
      </div>
    </header>
    <div class="body">
      <div class="content-wrapper">
        <!-- 左侧:上传图片 -->
        <div class="upload-section">
          <div class="upload-card">
            <div class="section-title">上传图片</div>
            <div class="upload-area" :class="{ 'has-grid': imagePath && !gridDrawing && gridRect }"
              @click="handleImageUploadClick" @dragover.prevent @drop="handleImageDrop"
              v-loading="loading">
              <div v-if="imagePath" class="close-btn" @click.stop="deleteImage">×</div>
              <div v-if="!imagePath" class="upload-placeholder">
                <div class="upload-icon">📷</div>
                <div class="upload-text">拖拽图片或点击上传</div>
                <div class="upload-button">上传</div>
              </div>
              <div v-if="imagePath" class="image-wrapper" :style="imageWrapperStyle">
                <img ref="gridImage" :src="imagePath" alt="" class="uploaded-image"
                  @mousedown="onGridMouseDown" @load="onImageLoad">
                <!-- 绘制中的网格预览 -->
                <div v-if="gridDrawing && drawRect" class="grid-overlay"
                  :style="{ left: drawRect.x + 'px', top: drawRect.y + 'px', width: drawRect.w + 'px', height: drawRect.h + 'px' }">
                  <div class="grid-lines"></div>
                </div>
                <!-- 已放置的网格 -->
                <div v-if="!gridDrawing && gridRect" class="grid-overlay"
                  :style="gridOverlayStyle"
                  @mousedown.stop="onGridAreaMouseDown">
                  <div class="grid-lines"
                    :style="{ backgroundImage: gridLinesBackground }"></div>
                  <div v-for="h in gridHandles" :key="h.dir"
                    class="grid-handle"
                    :class="'handle-' + h.dir"
                    @mousedown.stop.prevent="onHandleMouseDown($event, h.dir)"></div>
                </div>
                <!-- 可拖拽分割线（仅内部线） -->
                <template v-if="!gridDrawing && gridRect && rowSizes.length && colSizes.length">
                  <div v-for="line in internalHLines" :key="'h' + line.index"
                    class="grid-line-hit grid-line-h"
                    :style="internalHLineStyle(line)"
                    @mousedown.stop.prevent="onHLineMouseDown($event, line.index)"></div>
                  <div v-for="line in internalVLines" :key="'v' + line.index"
                    class="grid-line-hit grid-line-v"
                    :style="internalVLineStyle(line)"
                    @mousedown.stop.prevent="onVLineMouseDown($event, line.index)"></div>
                </template>
              </div>
            </div>
            <!-- 网格行列控制 -->
            <div v-if="imagePath" class="grid-controls" @click.stop @mousedown.stop>
              <div class="control-item">
                <span class="control-label">行数:</span>
                <el-input-number v-model="gridRows" :min="1" :max="20" size="mini" controls-position="right"></el-input-number>
              </div>
              <div class="control-item">
                <span class="control-label">列数:</span>
                <el-input-number v-model="gridCols" :min="1" :max="20" size="mini" controls-position="right"></el-input-number>
              </div>
              <div class="control-item">
                <span class="control-hint">{{ gridCellInfo }}</span>
              </div>
              <div v-if="gridRect" class="control-item">
                <el-button size="mini" type="text" @click="resetGrid">清除网格</el-button>
              </div>
            </div>
            <div class="upload-tips">
              <div class="tip-desc">在图片上拖拽绘制网格区域，拖拽分割线可单独调整每行/每列大小。</div>
              <div class="tip-desc">拖拽边角可调整整体区域，点击已有网格可重新绘制。</div>
            </div>
          </div>
        </div>

        <!-- 右侧:输出图片 -->
        <div class="output-section">
          <div class="output-card">
            <div class="section-title">输出图片 <span v-if="processedImages.length" class="image-count">{{ processedImages.length }}张</span></div>
            <div class="output-area">
              <div v-if="processedImages.length" class="close-btn" @click.stop="clearOutput">×</div>
              <div v-if="!processedImages.length && !tips" class="output-placeholder">
                <div class="output-icon">🖼️</div>
                <div class="output-text">在线分割</div>
              </div>
              <!-- 5列正方形预览网格 -->
              <div v-if="processedImages.length" class="image-grid">
                <div v-for="(url, index) in processedImages" :key="index" class="grid-cell">
                  <img :src="url" alt="" class="grid-cell-img" @click="previewImage(url)">
                  <div class="grid-cell-index">{{ index + 1 }}</div>
                </div>
              </div>
              <div v-if="tips" class="processing-mask">
                <div class="processing-text">{{ tips }}</div>
              </div>
            </div>
            <div class="output-tips">
              <div class="tip-desc">上传图像后，点击“生成图片”即可进行图片分割。</div>
              <div class="tip-desc">分割结果将以网格形式展示，点击图片可预览大图。</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部操作栏 -->
      <div class="bottom-actions">
        <div class="generate-btn" @click="start_process">生成图片</div>
        <div class="save-btn" @click="handleSave">保存图片</div>
      </div>
    </div>
    <ImageModal class="modal" :visible="isModalVisible" :img_url="imageUrl" @update:visible="isModalVisible = $event"
      :img_show="true" :video_show="false" :video_src="video_src"></ImageModal>
    <discussion-board class="discussion-board" :boardvisible="boardvisible" :disscussType="disscussType"
      @update:boardvisible="boardvisible = $event"></discussion-board>
  </div>
</template>
<script>
import { genOssImage } from '@/utils/ossPath'
import { v4 as uuidv4 } from 'uuid'
import { Message } from 'element-ui'
import { genPrompt } from '@/api/prompt'
import { getLastUsedBaseUrl, cudaIndexNum } from '@/utils/comfyUiRequest'
import axios from 'axios'
import cuda from '@/utils/checkCuda'
import { getOssClient } from '@/utils/oss'
import localStorageUtils from '@/utils/setLocalStorage'
import { getTaskEnd, getTaskStart } from '@/api/user'
import { T_QUALITY_OPT } from '@/enums/common/modules'
import { getImageSizeByLoading } from '@/utils/helper/function'
import getImageDimensions from '@/utils/getImageDimensions'
import theme from '@/utils/theme'
import { mapGetters } from 'vuex'
import imageSegWorkflow from '@/api/json_api/图片分割工作流.json'
export default {
  name: 'ImageSegmentation',
  data() {
    return {
      taskLogId: '',
      value: false,
      imageSrc: '',
      imagePath: '',
      processedImage: '',
      processedImages: [],
      ossZipPath: '',
      tips: false,
      current: '',
      processing: false,
      websocket: null,
      ws: null,
      clientId: '',
      loading: false,
      day: theme.day,
      night: theme.night,
      theme: true,
      isModalVisible: false,
      imageUrl: '',
      img_show: false,
      video_src: '',
      boardvisible: false,
      disscussType: null,
      // 网格相关
      gridRect: null,
      gridRows: 2,
      gridCols: 2,
      gridDrawing: false,
      drawStart: null,
      drawRect: null,
      gridInteraction: null,
      originalImageWidth: 0,
      originalImageHeight: 0,
      rowSizes: [],
      colSizes: [],
      resizeTick: 0
    }
  },
  computed: {
    ...mapGetters(['avatar']),
    gridOverlayStyle() {
      this.resizeTick // eslint-disable-line
      if (!this.gridRect) return {}
      const ws = this.getWrapperSize()
      return {
        left: (this.gridRect.x * ws.w) + 'px',
        top: (this.gridRect.y * ws.h) + 'px',
        width: (this.gridRect.w * ws.w) + 'px',
        height: (this.gridRect.h * ws.h) + 'px'
      }
    },
    gridLinesBackground() {
      this.resizeTick // eslint-disable-line
      if (!this.gridRect || !this.rowSizes.length) return ''
      const ws = this.getWrapperSize()
      let hStops = 'transparent'
      let hPos = 0
      for (let i = 0; i < this.gridRows - 1; i++) {
        hPos += (this.rowSizes[i] || 0) * ws.h
        hStops += `, transparent ${hPos - 1}px, rgba(0,0,0,0.85) ${hPos - 1}px, rgba(0,0,0,0.85) ${hPos}px, transparent ${hPos + 1}px`
      }
      let vStops = 'transparent'
      let vPos = 0
      for (let i = 0; i < this.gridCols - 1; i++) {
        vPos += (this.colSizes[i] || 0) * ws.w
        vStops += `, transparent ${vPos - 1}px, rgba(0,0,0,0.85) ${vPos - 1}px, rgba(0,0,0,0.85) ${vPos}px, transparent ${vPos + 1}px`
      }
      return `linear-gradient(to bottom, ${hStops}), linear-gradient(to right, ${vStops})`
    },
    gridHandles() {
      return [
        { dir: 'nw' }, { dir: 'n' }, { dir: 'ne' },
        { dir: 'w' }, { dir: 'e' },
        { dir: 'sw' }, { dir: 's' }, { dir: 'se' }
      ]
    },
    gridCellInfo() {
      this.resizeTick // eslint-disable-line
      if (!this.gridRect || !this.originalImageWidth) return ''
      const wPx = Math.round(this.gridRect.w * this.originalImageWidth)
      const hPx = Math.round(this.gridRect.h * this.originalImageHeight)
      return `网格 ${wPx}×${hPx}px (原图坐标) | 共 ${this.gridRows * this.gridCols} 格`
    },
    hLinePositions() {
      this.resizeTick // eslint-disable-line
      if (!this.gridRect || !this.rowSizes.length) return []
      const ws = this.getWrapperSize()
      const positions = []
      let y = this.gridRect.y
      for (let i = 0; i <= this.gridRows; i++) {
        positions.push(y * ws.h)
        if (i < this.gridRows) y += this.rowSizes[i]
      }
      return positions
    },
    vLinePositions() {
      this.resizeTick // eslint-disable-line
      if (!this.gridRect || !this.colSizes.length) return []
      const ws = this.getWrapperSize()
      const positions = []
      let x = this.gridRect.x
      for (let i = 0; i <= this.gridCols; i++) {
        positions.push(x * ws.w)
        if (i < this.gridCols) x += this.colSizes[i]
      }
      return positions
    },
    internalHLines() {
      return this.hLinePositions
        .map((pos, i) => ({ pos, index: i }))
        .filter(l => l.index > 0 && l.index < this.gridRows)
    },
    internalVLines() {
      return this.vLinePositions
        .map((pos, i) => ({ pos, index: i }))
        .filter(l => l.index > 0 && l.index < this.gridCols)
    }
  },
  watch: {
    gridRows(newVal) {
      if (this.gridRect) {
        this.rowSizes = Array(newVal).fill(this.gridRect.h / newVal)
      }
    },
    gridCols(newVal) {
      if (this.gridRect) {
        this.colSizes = Array(newVal).fill(this.gridRect.w / newVal)
      }
    }
  },
  created() {
    this.imagePath = localStorageUtils.getItem('imageseg')
    this.processedImage = localStorageUtils.getItem('imageseg_showimage')
    this.disscussType = T_QUALITY_OPT
    if (localStorageUtils.getItem('theme') !== null) {
      this.theme = localStorageUtils.getItem('theme')
    } else {
      this.theme = true
    }
  },
  mounted() {
    this._resizeHandler = () => { this.resizeTick++ }
    window.addEventListener('resize', this._resizeHandler)
    window.addEventListener('keydown', this.handleKeystart)
    window.addEventListener('keydown', this.handleKeyPaste)
    window.addEventListener('beforeunload', this.releaseCuda)
    this.$nextTick(() => {
      this.source_html()
    })
  },
  async beforeDestroy() {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler)
    }
    window.removeEventListener('keydown', this.handleKeystart)
    window.removeEventListener('keydown', this.handleKeyPaste)
    try {
      const index = await cudaIndexNum()
      if (index) {
        await cuda.changeCudaCurrentStatus(index, 'leisure')
        console.log('current cuda is leisure')
      }
    } catch (e) {
      console.log('no cuda is running')
    }
    window.removeEventListener('beforeunload', this.releaseCuda)
  },
  methods: {
    gohome() {
      this.$router.push({ path: '/' })
    },
    handleGoToImageLog() {
      const targetPath = '/userimagelog'
      if (this.$router.history.current.fullPath !== targetPath) {
        this.$router.push({ path: targetPath })
      }
    },
    handleOpenFeedback() {
      this.boardvisible = true
    },
    handelDissvisible(x) {
      if (x === 1) {
        this.boardvisible = true
      }
      if (x === 2) {
        Message.warning('😋开发者功能暂未开放,敬请期待~!')
      }
    },
    async releaseCuda() {
      try {
        const index = await cudaIndexNum()
        if (index) {
          await cuda.changeCudaCurrentStatus(index, 'leisure')
          console.log('current cuda is leisure')
        }
      } catch (e) {
        console.log('no cuda is running')
      }
    },
    show_video(event) {
      event.preventDefault()
      event.stopPropagation()
      this.isModalVisible = true
      this.video_src = ''
    },
    handleKeystart(event) {
      if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault()
        this.start_process()
      }
    },
    warningTips() {
      Message.warning('由于浏览器安全协议,请使用Ctrl + V 快捷键进行图片粘贴')
    },
    async handleKeyPaste(event) {
      if (event.ctrlKey && event.key === 'v') {
        try {
          const textarea = document.createElement('textarea')
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          textarea.focus()
          const clipboardData = await new Promise((resolve, reject) => {
            textarea.addEventListener('paste', () => {
              setTimeout(() => {
                resolve(textarea.value)
              }, 10)
            })
            setTimeout(() => {
              reject(new Error('粘贴操作超时'))
            }, 2000)
          })
          document.body.removeChild(textarea)
          const imageRegex = /\.(png|jpe?g|gif|bmp|webp)$/i
          if (!imageRegex.test(clipboardData)) {
            Message.error('剪切板中的内容不是有效的图片地址（支持的格式：.png, .jpg, .jpeg, .gif, .bmp, .webp)')
          } else {
            this.imagePath = clipboardData
            localStorageUtils.setItem('imageseg', clipboardData)
            Message.success('已从剪切板粘贴图片地址')
          }
        } catch (err) {
          Message.error('出现系统兼容性错误,请上报错误给开发者!')
        }
      }
    },
    async uploadToOSS(file, ossClient) {
      const tmp = '/input/'
      const fileName = file.name
      console.log('上传到OSS:', tmp + fileName)
      try {
        const result = await ossClient.put(tmp + fileName, file)
        return result.url
      } catch (err) {
        return null
      }
    },
    handleImageUploadClick() {
      if (this.imagePath) return
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.onchange = (event) => {
        const file = event.target.files[0]
        if (file) {
          this.processImage(file)
        }
      }
      input.click()
    },
    handleImageDrop(event) {
      event.preventDefault()
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.processImage(file)
      } else {
        Message.error('请上传有效的图片文件')
      }
    },
    async processImage(file) {
      this.loading = true
      const ossClient = await getOssClient()
      const imageUrl = await this.uploadToOSS(file, ossClient)
      try {
        const imageSize = await getImageDimensions(imageUrl)
        if (imageSize.width > 4000 || imageSize.height > 4000) {
          Message.error('图片尺寸过大,请重新上传!建议分辨率为4000*4000以内')
          this.loading = false
          return
        }
        this.originalImageWidth = imageSize.width
        this.originalImageHeight = imageSize.height
      } catch (error) {
        Message.error('获取图片尺寸失败:', error)
      }
      if (imageUrl) {
        this.imagePath = imageUrl
        this.resetGrid()
        localStorageUtils.setItem('imageseg', imageUrl)
        Message.success('图片加载成功,请在图片上拖拽绘制网格区域!')
      } else {
        Message.error('图片上传失败,请检查网络配置!')
      }
      const reader = new FileReader()
      reader.onload = (e) => {
        this.imageSrc = e.target.result
      }
      reader.readAsDataURL(file)
      this.loading = false
    },
    resetGrid() {
      this.gridRect = null
      this.gridDrawing = false
      this.drawRect = null
      this.gridInteraction = null
      this.rowSizes = []
      this.colSizes = []
    },
    getMousePos(e) {
      const wrapper = this.$refs.gridImage ? this.$refs.gridImage.parentElement : null
      if (!wrapper) return { x: 0, y: 0 }
      const rect = wrapper.getBoundingClientRect()
      return {
        x: Math.max(0, Math.min(e.clientX - rect.left, rect.width)),
        y: Math.max(0, Math.min(e.clientY - rect.top, rect.height))
      }
    },
    onImageLoad() {
      if (!this.originalImageWidth && this.imagePath) {
        const img = this.$refs.gridImage
        if (img && img.naturalWidth) {
          this.originalImageWidth = img.naturalWidth
          this.originalImageHeight = img.naturalHeight
          console.log('图片原始尺寸已获取:', img.naturalWidth, 'x', img.naturalHeight)
        }
      }
    },
    getImageRect() {
      const img = this.$refs.gridImage
      if (!img || !img.naturalWidth) return { left: 0, top: 0, width: 0, height: 0 }
      const wrapper = img.parentElement
      if (!wrapper) return { left: 0, top: 0, width: 0, height: 0 }
      const wr = wrapper.getBoundingClientRect()
      const ir = img.getBoundingClientRect()
      return {
        left: ir.left - wr.left,
        top: ir.top - wr.top,
        width: ir.width,
        height: ir.height
      }
    },
    getWrapperSize() {
      const rect = this.getImageRect()
      return { w: rect.width || 1, h: rect.height || 1 }
    },
    imageWrapperStyle() {
      this.resizeTick // eslint-disable-line
      const rect = this.getImageRect()
      if (!rect.width) return {}
      return {
        left: rect.left + 'px',
        top: rect.top + 'px',
        width: rect.width + 'px',
        height: rect.height + 'px'
      }
    },
    internalHLineStyle(line) {
      this.resizeTick // eslint-disable-line
      const ws = this.getWrapperSize()
      return {
        top: line.pos + 'px',
        left: (this.gridRect.x * ws.w) + 'px',
        width: (this.gridRect.w * ws.w) + 'px'
      }
    },
    internalVLineStyle(line) {
      this.resizeTick // eslint-disable-line
      const ws = this.getWrapperSize()
      return {
        left: line.pos + 'px',
        top: (this.gridRect.y * ws.h) + 'px',
        height: (this.gridRect.h * ws.h) + 'px'
      }
    },
    onGridMouseDown(e) {
      if (!this.imagePath) return
      if (this.gridRect) {
        this.resetGrid()
      }
      e.preventDefault()
      const pos = this.getMousePos(e)
      const ws = this.getWrapperSize()
      this.gridDrawing = true
      this.drawStart = pos
      this.drawRect = { x: pos.x, y: pos.y, w: 0, h: 0 }
      const onDrawMove = (ev) => {
        const cur = this.getMousePos(ev)
        this.drawRect = {
          x: Math.min(this.drawStart.x, cur.x),
          y: Math.min(this.drawStart.y, cur.y),
          w: Math.abs(cur.x - this.drawStart.x),
          h: Math.abs(cur.y - this.drawStart.y)
        }
      }
      const onDrawUp = () => {
        if (this.drawRect && this.drawRect.w > 10 && this.drawRect.h > 10) {
          this.gridRect = {
            x: this.drawRect.x / ws.w,
            y: this.drawRect.y / ws.h,
            w: this.drawRect.w / ws.w,
            h: this.drawRect.h / ws.h
          }
          this.rowSizes = Array(this.gridRows).fill(this.gridRect.h / this.gridRows)
          this.colSizes = Array(this.gridCols).fill(this.gridRect.w / this.gridCols)
        }
        this.gridDrawing = false
        this.drawStart = null
        this.drawRect = null
        window.removeEventListener('mousemove', onDrawMove)
        window.removeEventListener('mouseup', onDrawUp)
      }
      window.addEventListener('mousemove', onDrawMove)
      window.addEventListener('mouseup', onDrawUp)
    },
    onGridAreaMouseDown(e) {
      if (!this.gridRect) return
      e.preventDefault()
      this.resetGrid()
      const pos = this.getMousePos(e)
      const ws = this.getWrapperSize()
      this.gridDrawing = true
      this.drawStart = pos
      this.drawRect = { x: pos.x, y: pos.y, w: 0, h: 0 }
      const onDrawMove = (ev) => {
        const cur = this.getMousePos(ev)
        this.drawRect = {
          x: Math.min(this.drawStart.x, cur.x),
          y: Math.min(this.drawStart.y, cur.y),
          w: Math.abs(cur.x - this.drawStart.x),
          h: Math.abs(cur.y - this.drawStart.y)
        }
      }
      const onDrawUp = () => {
        if (this.drawRect && this.drawRect.w > 10 && this.drawRect.h > 10) {
          this.gridRect = {
            x: this.drawRect.x / ws.w,
            y: this.drawRect.y / ws.h,
            w: this.drawRect.w / ws.w,
            h: this.drawRect.h / ws.h
          }
          this.rowSizes = Array(this.gridRows).fill(this.gridRect.h / this.gridRows)
          this.colSizes = Array(this.gridCols).fill(this.gridRect.w / this.gridCols)
        }
        this.gridDrawing = false
        this.drawStart = null
        this.drawRect = null
        window.removeEventListener('mousemove', onDrawMove)
        window.removeEventListener('mouseup', onDrawUp)
      }
      window.addEventListener('mousemove', onDrawMove)
      window.addEventListener('mouseup', onDrawUp)
    },
    onHandleMouseDown(e, dir) {
      if (!this.gridRect) return
      e.preventDefault()
      const startRect = { ...this.gridRect }
      const startRowSizes = [...this.rowSizes]
      const startColSizes = [...this.colSizes]
      const startPos = this.getMousePos(e)
      const ws = this.getWrapperSize()
      const onMove = (ev) => {
        const cur = this.getMousePos(ev)
        const dx = (cur.x - startPos.x) / ws.w
        const dy = (cur.y - startPos.y) / ws.h
        let { x, y, w, h } = startRect
        if (dir.includes('w')) { x += dx; w -= dx }
        if (dir.includes('e')) { w += dx }
        if (dir.includes('n')) { y += dy; h -= dy }
        if (dir.includes('s')) { h += dy }
        if (dir === 'n' || dir === 's') { x = startRect.x; w = startRect.w }
        if (dir === 'e' || dir === 'w') { y = startRect.y; h = startRect.h }
        const minRW = 20 / ws.w
        const minRH = 20 / ws.h
        if (w < minRW) { w = minRW; if (dir.includes('w')) x = startRect.x + startRect.w - minRW }
        if (h < minRH) { h = minRH; if (dir.includes('n')) y = startRect.y + startRect.h - minRH }
        x = Math.max(0, x)
        y = Math.max(0, y)
        if (x + w > 1) w = 1 - x
        if (y + h > 1) h = 1 - y
        this.gridRect = { x, y, w, h }
        if (startRowSizes.length) {
          const scaleY = h / startRect.h
          this.rowSizes = startRowSizes.map(s => s * scaleY)
        }
        if (startColSizes.length) {
          const scaleX = w / startRect.w
          this.colSizes = startColSizes.map(s => s * scaleX)
        }
      }
      const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    getGridData() {
      if (!this.gridRect || !this.originalImageWidth) return null
      return {
        original_image_width: this.originalImageWidth,
        original_image_height: this.originalImageHeight,
        grid: {
          rows: this.gridRows,
          cols: this.gridCols,
          x: Math.round(this.gridRect.x * this.originalImageWidth),
          y: Math.round(this.gridRect.y * this.originalImageHeight),
          width: Math.round(this.gridRect.w * this.originalImageWidth),
          height: Math.round(this.gridRect.h * this.originalImageHeight),
          row_sizes: this.rowSizes.map(s => Math.round(s * this.originalImageHeight)),
          col_sizes: this.colSizes.map(s => Math.round(s * this.originalImageWidth))
        }
      }
    },
    onHLineMouseDown(e, lineIndex) {
      if (!this.gridRect) return
      e.preventDefault()
      const startPos = this.getMousePos(e)
      const ws = this.getWrapperSize()
      const startGridH = this.gridRect.h
      const startRowSizes = [...this.rowSizes]
      const onMove = (ev) => {
        const cur = this.getMousePos(ev)
        const dy = (cur.y - startPos.y) / ws.h
        const newSize = startRowSizes[lineIndex - 1] + dy
        const minRH = 5 / ws.h
        if (newSize >= minRH) {
          this.rowSizes[lineIndex - 1] = newSize
          const newH = startGridH + dy
          const minH = 20 / ws.h
          if (newH >= minH) {
            this.gridRect = { ...this.gridRect, h: newH }
          }
        }
      }
      const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    onVLineMouseDown(e, lineIndex) {
      if (!this.gridRect) return
      e.preventDefault()
      const startPos = this.getMousePos(e)
      const ws = this.getWrapperSize()
      const startGridW = this.gridRect.w
      const startColSizes = [...this.colSizes]
      const onMove = (ev) => {
        const cur = this.getMousePos(ev)
        const dx = (cur.x - startPos.x) / ws.w
        const newSize = startColSizes[lineIndex - 1] + dx
        const minRW = 5 / ws.w
        if (newSize >= minRW) {
          this.colSizes[lineIndex - 1] = newSize
          const newW = startGridW + dx
          const minW = 20 / ws.w
          if (newW >= minW) {
            this.gridRect = { ...this.gridRect, w: newW }
          }
        }
      }
      const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    deleteImage() {
      this.imagePath = ''
      this.processing = false
      this.tips = false
      this.resetGrid()
      this.originalImageWidth = 0
      this.originalImageHeight = 0
      localStorage.removeItem('imageseg')
      localStorage.removeItem('imageseg_showimage')
    },
    previewImage(url) {
      this.imageUrl = url
      this.isModalVisible = true
    },
    clearOutput() {
      this.processedImage = ''
      this.processedImages = []
      this.processing = false
      this.tips = false
      localStorage.removeItem('imageseg_showimage')
    },
    handleSave() {
      if (!this.processedImages.length) {
        Message.warning('😋请先生成图片,然后再进行保存~!')
        return
      }
      if (this.ossZipPath) {
        const zipUrl = genOssImage(this.ossZipPath)
        const link = document.createElement('a')
        link.href = zipUrl
        link.download = this.ossZipPath.split('/').pop() || 'grid_split.zip'
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        Message.success('开始下载压缩包!')
      } else {
        Message.warning('未找到压缩包路径，请重新生成')
      }
    },
    replaceWorkflowVariables(workflow, gridData, imageUrl) {
      const wf = JSON.parse(JSON.stringify(workflow))
      const timestamp = Date.now()
      const randomNum = Math.floor(Math.random() * 99999)
      const baseName = `tmp-comfyui/grid_split/${timestamp}_${randomNum}`
      console.log('🔍 开始替换图片分割工作流变量...')
      for (const nodeId in wf) {
        const node = wf[nodeId]
        if (node.inputs) {
          for (const key in node.inputs) {
            const val = node.inputs[key]
            if (typeof val === 'string') {
              if (val.includes('{{GRID_JSON}}')) {
                node.inputs[key] = JSON.stringify(gridData)
                console.log(`✅ 节点${nodeId}.grid_json: 已注入网格数据`)
              } else if (val.includes('{{IMAGE_URL}}')) {
                node.inputs[key] = imageUrl
                console.log(`✅ 节点${nodeId}.image_url: {{IMAGE_URL}} -> ${imageUrl}`)
              } else if (val.includes('{{OSS_ZIP_PATH}}')) {
                const zipPath = `${baseName}.zip`
                node.inputs[key] = zipPath
                this.ossZipPath = zipPath
                console.log(`✅ 节点${nodeId}.oss_path: {{OSS_ZIP_PATH}} -> ${zipPath}`)
              } else if (val.includes('{{OUTPUT_FILENAME}}')) {
                const totalImages = this.gridRows * this.gridCols
                const filenames = []
                for (let i = 0; i < totalImages; i++) {
                  filenames.push(`${baseName}_${i + 1}.png`)
                }
                node.inputs[key] = JSON.stringify(filenames)
                console.log(`✅ 节点${nodeId}.filename: {{OUTPUT_FILENAME}} -> ${totalImages}个文件名`)
              }
            }
          }
        }
      }
      console.log('🔍 替换后的工作流:', JSON.stringify(wf, null, 2))
      return wf
    },
    start_process() {
      this.processedImage = ''
      this.processedImages = []
      if (this.imagePath === '' || this.imagePath === null) {
        Message.warning('请先选择图像文件')
        return
      }
      if (this.processing) {
        Message.warning('任务已在进行中，请勿重复提交以免造成错误!')
        return
      }
      this.processing = true
      this.tips = '任务正在生成,请耐心等待.....'
      console.log('========== 图片分割 - 网格数据 ==========')
      console.log('gridRect:', this.gridRect)
      console.log('originalImageWidth:', this.originalImageWidth)
      console.log('originalImageHeight:', this.originalImageHeight)
      console.log('rowSizes:', JSON.stringify(this.rowSizes))
      console.log('colSizes:', JSON.stringify(this.colSizes))
      const gridData = this.getGridData()
      if (!gridData) {
        if (!this.gridRect) console.warn('getGridData返回null: gridRect为空,请先在图片上拖拽绘制网格')
        if (!this.originalImageWidth) console.warn('getGridData返回null: originalImageWidth为0,图片尺寸未获取成功')
      } else {
        console.log('网格数据(原图像素):', JSON.stringify(gridData, null, 2))
      }
      console.log('========================================')
      const promptData = this.replaceWorkflowVariables(imageSegWorkflow, gridData, this.imagePath)
      this.clientId = uuidv4()
      const data = {
        prompt: promptData,
        client_id: this.clientId
      }
      const sendPromptRequest = async () => {
        try {
          console.log('get prompt response', data)
          return await genPrompt(data)
        } catch (error) {
          console.error('发送作图请求失败：', error)
          if (error.response) {
            const status = error.response.status
            const errorData = error.response.data
            console.error('请求失败详情：', { status, errorData })
            if (status === 400) {
              Message.error('请求参数错误(400)，请检查输入参数是否正确')
            }
          } else if (error.request) {
            console.error('网络错误或CORS拦截，请检查网络或查看控制台')
            Message.error('网络连接异常，请检查网络或查看控制台')
          } else {
            console.error('请求配置错误：', error.message)
            Message.error('请求配置错误，请联系开发者')
          }
          this.processing = false
          this.tips = false
          return null
        }
      }

      const fetchImageFilename = async (promptId) => {
        try {
          const historyRequest = axios.create({
            baseURL: getLastUsedBaseUrl(),
            timeout: 10000,
            headers: {
              'Content-Type': 'application/json'
            }
          })

          const response = await historyRequest.get(`/history/${promptId}`)
          const promptInfo = response.data[promptId]
          console.log('📦 工作流执行完成，历史数据:', promptInfo)
          console.log('📦 status:', promptInfo ? promptInfo.status : 'promptInfo为空')
          console.log('📦 outputs:', promptInfo && promptInfo.outputs ? Object.keys(promptInfo.outputs) : '无outputs')
          if (promptInfo && promptInfo.status && promptInfo.status.messages) {
            console.error('📦 ComfyUI错误详情:', JSON.stringify(promptInfo.status.messages, null, 2))
          }

          const isError = promptInfo && promptInfo.status && promptInfo.status.status_str === 'error'
          const hasOutputs = promptInfo && promptInfo.outputs && Object.keys(promptInfo.outputs).length > 0
          const isSuccess = promptInfo && promptInfo.status && promptInfo.status.status_str === 'success'

          if (isError) {
            console.error('❌ ComfyUI工作流执行失败，请查看上方错误详情')
            Message.error('图像分割失败，请查看控制台错误详情')
            this.processing = false
            this.tips = false
          } else if (isSuccess || hasOutputs) {
            if (!isSuccess) console.warn('⚠️ 状态非success但有outputs，尝试提取图片')
            const imageUrls = []
            // 从节点10的输出中收集所有图片URL
            if (promptInfo.outputs && promptInfo.outputs['10']) {
              const node10Output = promptInfo.outputs['10']
              console.log('📦 节点10输出:', node10Output)
              // 尝试从images字段获取
              if (node10Output.images && Array.isArray(node10Output.images)) {
                node10Output.images.forEach(img => {
                  if (img.path) imageUrls.push(genOssImage(img.path))
                  else if (img.filename) imageUrls.push(genOssImage(img.filename))
                })
              }
              // 尝试从output字段获取
              if (node10Output.output && Array.isArray(node10Output.output)) {
                node10Output.output.forEach(item => {
                  if (typeof item === 'string') imageUrls.push(genOssImage(item))
                })
              }
            }
            // 回退: 从prompt输入中获取文件名
            if (!imageUrls.length && promptInfo.prompt) {
              // ComfyUI history返回格式: [count, promptId, {nodes}, metadata, execOrder]
              // 节点数据在 prompt[2]，是对象 {nodeId: {class_type, inputs, ...}}
              const nodesObj = Array.isArray(promptInfo.prompt) ? promptInfo.prompt[2] : promptInfo.prompt
              console.log('📦 nodesObj类型:', typeof nodesObj, 'keys:', nodesObj ? Object.keys(nodesObj) : 'null')
              if (nodesObj && typeof nodesObj === 'object') {
                for (const nodeId in nodesObj) {
                  const nodeData = nodesObj[nodeId]
                  console.log(`📦 节点${nodeId} class_type:`, nodeData.class_type)
                  if (nodeData.class_type === 'AC_Oss上传图像' && nodeData.inputs) {
                    const fn = nodeData.inputs.filename
                    console.log('📦 找到AC_Oss节点, filename:', fn)
                    try {
                      const parsed = JSON.parse(fn)
                      if (Array.isArray(parsed)) {
                        parsed.forEach(p => imageUrls.push(genOssImage(p)))
                      }
                    } catch (e) {
                      if (typeof fn === 'string' && fn.length > 0) {
                        imageUrls.push(genOssImage(fn))
                      }
                    }
                  }
                }
              }
            }
            console.log(`📦 收集到 ${imageUrls.length} 张图片URL:`, imageUrls)
            if (imageUrls.length > 0) {
              this.processedImages = imageUrls
              this.processedImage = imageUrls[0]
              localStorageUtils.setItem('imageseg_showimage', imageUrls[0])
              // 上报任务结果
              const imgInfo = await getImageSizeByLoading(imageUrls[0])
              const info = { details: imageUrls.map(url => ({ path: url, imgInfo })) }
              getTaskEnd({ result: JSON.stringify(info) }, this.taskLogId)
            } else {
              console.warn('未找到输出图片URL')
              Message.warning('分割完成但未找到输出图片，请查看控制台日志')
            }
            this.processing = false
            this.tips = false
          } else {
            console.error('任务执行失败或状态异常：', promptInfo)
            Message.error('图像分割失败，请重试')
            this.processing = false
            this.tips = false
          }
        } catch (error) {
          console.error('获取图像文件名失败：', error)
          if (error.response) {
            console.error('响应错误详情：', error.response.status, error.response.data)
            Message.error(`获取结果失败(${error.response.status})，请重试`)
          } else if (error.request) {
            console.error('请求无响应，请检查网络或查看Network面板')
            Message.error('网络异常，无法获取结果')
          } else {
            Message.error('获取结果失败，请重试')
          }
          this.processing = false
          this.tips = false
        }
      }

      const listenToProgress = (promptId) => {
        this.ws = new WebSocket(`${getLastUsedBaseUrl()}/ws?clientId=${this.clientId}`)
        const wsTimeout = setTimeout(() => {
          Message.warning('网络连接超时,请重新尝试!')
          this.ws.close()
        }, 2000)

        this.ws.onopen = () => {
          console.log('提交服务器开始生成...')
          getTaskStart({
            type: T_QUALITY_OPT
          }).then(rep => {
            this.taskLogId = rep.data.task.id
          })
          clearTimeout(wsTimeout)
        }
        this.ws.onmessage = async (event) => {
          clearTimeout(wsTimeout)
          const data = event.data
          if (typeof data === 'string') {
            try {
              const msg = JSON.parse(event.data)
              console.log(`这是返回${msg}`)

              if (msg.type === 'executing') {
                const data = msg.data
                if (data.node === null && data.prompt_id === promptId) {
                  this.ws.close()
                  await fetchImageFilename(promptId)
                }
              } else if (msg.type === 'progress') {
                const value = msg.data.value
                const max = msg.data.max
                const current = (value / max * 100).toFixed(2)
                this.tips = `大约完成：${current}%`
              }
            } catch (error) {
              console.log('获取结果失败，请重试')
              this.tips = false
            }
          }
        }
        this.ws.onclose = async () => {
          this.processing = false
          this.tips = false
          try {
            const index = cudaIndexNum()
            await cuda.changeCudaCurrentStatus(index, 'leisure')
          } catch (error) { console.log(error) }
        }
      }

      (async () => {
        const result = await sendPromptRequest()
        if (result && result.prompt_id) {
          listenToProgress(result.prompt_id)
        }
      })()
    },
    async getTaskEnd(tmp, taskLogId) {
      const imgInfo = await getImageSizeByLoading(tmp)
      const info = {
        details: [{ path: tmp, imgInfo: imgInfo }]
      }
      getTaskEnd({ result: JSON.stringify(info) }, taskLogId)
    },
    source_html() {
      const main = document.querySelector('.content-wrapper')
      if (main) {
        main.style.opacity = this.theme === false ? 0.75 : 1
      }
    }
  }
}
</script>
<style lang="less" scoped>
@import "@/style/common.less";
@import "@/assets/fonts/style.css";
@import "@/assets/font_sczlgzino/iconfont.css";

@keyframes ip_move {
  0% {
    padding-bottom: 5px;
  }

  50% {
    padding-bottom: 10px;
  }

  100% {
    padding-bottom: 5px;
  }
}

@keyframes opacity {
  0% {
    opacity: 0.4;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 0.4;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  font-family: "icomoon";
  overflow: hidden;
}

.active {
  transition: all 0.5s;
  background-color: @basicselect;
  color: @font_black;
}

.el-message--warning {
  background-color: #fff3cd !important;
  color: #856404 !important;
  border: 1px solid #ffeeba !important;
  font-weight: bold;
}

.iconfont {
  font-family: "iconfont" !important;
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-size: 25px;
}

.app-container {
  width: 100vw;
  height: 100vh;
  background: #f5f7fa;
  overflow: hidden;
}

// Header样式
header {
  height: 95px;
  width: 100%;
  padding: 10px 30px;
  font-family: "icomoon";
  font-size: 18px;
  border-bottom: 1px solid #e5e7eb;
  background: white;

  .header-content {
    display: flex;
    align-items: center;
    height: 100%;
  }

  .logo-section {
    width: 230px;
    height: 75px;
    display: flex;
    align-items: center;
    padding: 10px;

    img {
      width: 230px;
      object-fit: cover;
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        transform: scale(1.05);
      }
    }
  }

  .header-spacer {
    flex: 1;
  }

  .header-actions {
    display: flex;
    gap: 20px;
    align-items: center;

    .home-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 10px;
      background-color: #ff8c42;
      color: white;
      cursor: pointer;
      transition: all 0.3s;
      font-size: 16px;
      font-weight: 500;
      white-space: nowrap;

      .iconfont {
        font-size: 20px;
      }

      &:hover {
        background-color: #ff7a2e;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(255, 140, 66, 0.3);
      }
    }

    .header-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 10px;
      background-color: rgba(39, 41, 46, 0.05);
      cursor: pointer;
      transition: all 0.3s;
      font-size: 16px;
      color: #333;
      white-space: nowrap;

      .iconfont {
        font-size: 20px;
      }

      &:hover {
        background-color: #f0f0f0;
        transform: translateY(-2px);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
      }
    }

    .user-avatar {
      display: flex;
      width: 50px;
      height: 50px;
      border: 2px solid #e5e7eb;
      border-radius: 50%;
      overflow: hidden;
      justify-content: center;
      align-items: center;

      img {
        cursor: pointer;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: all 0.3s;

        &:hover {
          transform: scale(1.1);
        }
      }
    }
  }
}

.body {
  width: 100vw;
  height: calc(100vh - 95px);
  background: #ffffff;
  padding: 40px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: @basicselect transparent;

  .content-wrapper {
    display: flex;
    gap: 50px;
    max-width: 1600px;
    margin: 0 auto;
    height: calc(100% - 140px);
  }

  .upload-section,
  .output-section {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .section-title {
    font-size: 28px;
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
  }

  .upload-card,
  .output-card {
    background: white;
    border-radius: 30px;
    padding: 30px;
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: 600px;
  }

  .upload-area {
    position: relative;
    flex: 1;
    background: #f8f9fa;
    border: 2px dashed #dee2e6;
    border-radius: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s;
    overflow: hidden;
    min-height: 400px;
    height: 400px;

    &:hover {
      border-color: #ff8c42;
      background: #fff8f3;
    }

    .close-btn {
      position: absolute;
      top: 15px;
      right: 15px;
      width: 32px;
      height: 32px;
      background: rgba(0, 0, 0, 0.5);
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      cursor: pointer;
      z-index: 10;
      transition: all 0.3s;

      &:hover {
        background: rgba(255, 0, 0, 0.8);
        transform: scale(1.1);
      }
    }

    .upload-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 15px;

      .upload-icon {
        font-size: 60px;
      }

      .upload-text {
        font-size: 16px;
        color: #6c757d;
      }

      .upload-button {
        padding: 10px 30px;
        background: #ff8c42;
        color: white;
        border-radius: 25px;
        font-size: 16px;
        transition: all 0.3s;

        &:hover {
          background: #ff7a2e;
          transform: translateY(-2px);
        }
      }
    }

    .uploaded-image {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }

    &.has-grid {
      cursor: default;
    }

    .image-wrapper img {
      cursor: crosshair;
    }

    .image-wrapper {
      position: absolute;
    }

    .grid-overlay {
      position: absolute;
      border: 2px dashed rgba(255, 140, 66, 0.9);
      background: rgba(255, 140, 66, 0.05);
      z-index: 5;
      pointer-events: auto;

      .grid-lines {
        width: 100%;
        height: 100%;
      }

      .grid-handle {
        position: absolute;
        width: 12px;
        height: 12px;
        background: #ff8c42;
        border: 2px solid #fff;
        border-radius: 2px;
        z-index: 6;

        &.handle-nw { top: -6px; left: -6px; cursor: nw-resize; }
        &.handle-n { top: -6px; left: 50%; margin-left: -6px; cursor: n-resize; }
        &.handle-ne { top: -6px; right: -6px; cursor: ne-resize; }
        &.handle-w { top: 50%; left: -6px; margin-top: -6px; cursor: w-resize; }
        &.handle-e { top: 50%; right: -6px; margin-top: -6px; cursor: e-resize; }
        &.handle-sw { bottom: -6px; left: -6px; cursor: sw-resize; }
        &.handle-s { bottom: -6px; left: 50%; margin-left: -6px; cursor: s-resize; }
        &.handle-se { bottom: -6px; right: -6px; cursor: se-resize; }
      }
    }

    .grid-line-hit {
      position: absolute;
      z-index: 10;

      &.grid-line-h {
        height: 10px;
        margin-top: -5px;
        cursor: ns-resize;

        &::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          top: 50%;
          height: 1px;
          background: rgba(0, 0, 0, 0.3);
          transform: translateY(-50%);
        }

        &:hover::after {
          height: 3px;
          background: rgba(0, 0, 0, 0.6);
        }
      }

      &.grid-line-v {
        width: 10px;
        margin-left: -5px;
        cursor: ew-resize;

        &::after {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          left: 50%;
          width: 1px;
          background: rgba(0, 0, 0, 0.3);
          transform: translateX(-50%);
        }

        &:hover::after {
          width: 3px;
          background: rgba(0, 0, 0, 0.6);
        }
      }
    }
  }

  .grid-controls {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 12px 0;
    flex-wrap: wrap;

    .control-item {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .control-label {
      font-size: 14px;
      font-weight: 600;
      color: #495057;
    }

    .control-hint {
      font-size: 13px;
      color: #868e96;
    }
  }

  .upload-tips {
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-size: 14px;
    color: #6c757d;

    .tip-item {
      display: flex;
      gap: 5px;

      .tip-label {
        font-weight: 600;
        color: #495057;
      }

      .tip-value {
        color: #ff8c42;
      }
    }

    .tip-desc {
      line-height: 1.6;
      color: #868e96;

      .link-text {
        color: #ff8c42;
        cursor: pointer;
        text-decoration: underline;
        &:hover {
          color: #ff7a2e;
        }
      }
    }
  }

  .output-area {
    position: relative;
    flex: 1;
    background: #f8f9fa;
    border: 2px dashed #dee2e6;
    border-radius: 30px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 400px;
    height: 400px;
    padding: 10px;

    .close-btn {
      position: absolute;
      top: 15px;
      right: 15px;
      width: 32px;
      height: 32px;
      background: rgba(0, 0, 0, 0.5);
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      cursor: pointer;
      z-index: 10;
      transition: all 0.3s;

      &:hover {
        background: rgba(255, 0, 0, 0.8);
        transform: scale(1.1);
      }
    }

    .image-count {
      font-size: 12px;
      color: #ff8c42;
      font-weight: normal;
    }

    .output-placeholder {
      margin: auto;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 15px;

      .output-icon {
        font-size: 60px;
        opacity: 0.3;
      }

      .output-text {
        font-size: 16px;
        color: #adb5bd;
      }
    }

    .output-image {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }

    .image-grid {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 6px;
      width: 100%;
      align-content: start;
    }

    .grid-cell {
      position: relative;
      width: 100%;
      padding-bottom: 100%;
      background: #e9ecef;
      border-radius: 6px;
      overflow: hidden;
      cursor: pointer;

      &:hover {
        outline: 2px solid #ff8c42;
      }

      .grid-cell-img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .grid-cell-index {
        position: absolute;
        top: 2px;
        left: 2px;
        background: rgba(0, 0, 0, 0.5);
        color: white;
        font-size: 10px;
        padding: 1px 4px;
        border-radius: 3px;
        line-height: 1.2;
      }
    }

    .processing-mask {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 5;

      .processing-text {
        font-size: 18px;
        color: #ff8c42;
        font-weight: 600;
      }
    }
  }

  .output-tips {
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-size: 14px;
    color: #6c757d;

    .tip-desc {
      line-height: 1.6;
      color: #868e96;
    }
  }

  .bottom-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 30px;
    margin-top: 70px;
    max-width: 1600px;
    margin-left: auto;
    margin-right: auto;

    .generate-btn {
      padding: 12px 100px;
      background: #ff8c42;
      color: white;
      border-radius: 30px;
      font-size: 18px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s;
      box-shadow: 0 4px 15px rgba(255, 140, 66, 0.3);

      &:hover {
        background: #ff7a2e;
        transform: translateY(-2px);
        box-shadow: 0 6px 15px rgba(255, 140, 66, 0.6);
      }

      &:active {
        transform: translateY(0);
      }
    }

    .save-btn {
      padding: 12px 40px;
      background: white;
      color: #ff8c42;
      border: 2px solid #ff8c42;
      border-radius: 30px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        background: #ff8c42;
        color: white;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(255, 140, 66, 0.3);
      }
    }
  }
}
</style>
